import { Listbox, Portal, Transition } from "@headlessui/react";
import { useCallback, useEffect, useState } from "react";
import { cn } from "../cn";

const GAP = 4;
const DEFAULT_MAX_HEIGHT_PX = 240;

type PortaledListboxOptionsProps = {
  open: boolean;
  buttonRef: React.RefObject<HTMLElement | null>;
  children: React.ReactNode;
  className?: string;
  /** Desired max height in px; clamped to available viewport space. */
  maxHeightPx?: number;
};

/**
 * Renders Listbox.Options in a portal with fixed positioning so they escape
 * modal overflow clipping. Use z above modal shell (z-[200]).
 */
export function PortaledListboxOptions({
  open,
  buttonRef,
  children,
  className,
  maxHeightPx = DEFAULT_MAX_HEIGHT_PX,
}: PortaledListboxOptionsProps) {
  const [position, setPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
    maxHeight: maxHeightPx,
    placeAbove: false,
  });

  const updatePosition = useCallback(() => {
    if (!buttonRef?.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const spaceBelow = Math.max(0, window.innerHeight - rect.bottom - GAP);
    const spaceAbove = Math.max(0, rect.top - GAP);
    const placeAbove =
      spaceBelow < Math.min(maxHeightPx, 120) && spaceAbove > spaceBelow;
    const available = placeAbove ? spaceAbove : spaceBelow;
    const maxHeight = Math.max(80, Math.min(maxHeightPx, available));

    const width = Math.min(rect.width, window.innerWidth - GAP * 2);
    const left = Math.min(
      Math.max(GAP, rect.left),
      window.innerWidth - width - GAP
    );

    setPosition({
      top: placeAbove ? rect.top - GAP : rect.bottom + GAP,
      left,
      width,
      maxHeight,
      placeAbove,
    });
  }, [buttonRef, maxHeightPx]);

  useEffect(() => {
    if (open) updatePosition();
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return;
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [open, updatePosition]);

  return (
    <Portal>
      <Transition
        show={open}
        leave="transition ease-out-strong duration-100"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <Listbox.Options
          static
          className={cn(
            "dropdown-menu fixed z-[220] overflow-auto text-[13px] focus:outline-none",
            position.placeAbove && "origin-bottom",
            className
          )}
          style={{
            top: position.top,
            left: position.left,
            width: position.width,
            maxHeight: position.maxHeight,
            transform: position.placeAbove ? "translateY(-100%)" : undefined,
          }}
        >
          {children}
        </Listbox.Options>
      </Transition>
    </Portal>
  );
}
